import {
	Button,
	DownloadButton,
	DownloadPersonalInfoButton,
	Input,
	Loading,
	PrintButton,
} from '@/components';
import { TextMessage } from '@/constant';
import { defineDocumentAuditDownload } from '@/services';
import {
	allowOnlyNumeric,
	findPosition,
	NOOP,
	onDownloadFileLocal,
	showAlert,
} from '@/utils';
import { handlePrint, printDocument } from '@/utils/pdf.util';
import {
	Children,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { InputRef } from 'antd';

import { isArray } from 'ramda-adjunct';

import { ContainerPdfProps } from './container-pdf.type';

const ContainerPdf = (props: ContainerPdfProps) => {
	const {
		children,
		fileName,
		pageRefs,
		onSubmit,
		hasErrorToDownload,
		hasFile,
	} = props;

	const [page, setPage] = useState('1');
	const [isLoading, setIsLoading] = useState(false);

	const inputRef = useRef<InputRef>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const printIframeRef = useRef<HTMLIFrameElement | null>(null);
	const printUrlRef = useRef<string | null>(null);

	const downloadPersonalInfoButtonRef = useRef<{ openModal: () => void }>(null);

	const count = Children.count(children);

	const handlePageChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setPage(allowOnlyNumeric(e.target.value));
		},
		[]
	);
	const onPressEnter = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			const newPage = Math.min(
				Number((e.target as HTMLInputElement).value),
				count
			);
			setPage(newPage.toLocaleString());
			inputRef.current?.input?.blur();
			isArray(pageRefs?.current) &&
				(pageRefs?.current[newPage - 1] as HTMLDivElement)?.scrollIntoView({
					behavior: 'smooth',
				});
		},
		[count, inputRef, children]
	);

	const handleScroll = useCallback(() => {
		const arrayOffset =
			(isArray(pageRefs?.current) &&
				pageRefs?.current?.map(
					(item) => (item as HTMLDivElement)?.offsetTop
				)) ||
			[];

		const { scrollTop, clientHeight } = containerRef.current as HTMLDivElement;
		const containerBottom = scrollTop + clientHeight;
		const pageViewport = findPosition(arrayOffset, containerBottom);
		setPage(pageViewport.toLocaleString());
	}, [children, pageRefs?.current]);

	const handleDownloadFile = useCallback(
		async (reason?: string, callback?: () => void) => {
			try {
				callback?.();
				setIsLoading(true);
				const filePdf = await printDocument(pageRefs?.current || [], fileName);
				await onDownloadFileLocal(filePdf);

				await defineDocumentAuditDownload({
					downloadReason: reason,
					downloadFileName: fileName,
				});
			} catch (error) {
				console.error('Error downloading file:', error);
			} finally {
				setIsLoading(false);
			}
		},
		[fileName, pageRefs]
	);

	const onBeforeDownloadOpen = useCallback(() => {
		if (hasErrorToDownload) {
			showAlert(
				TextMessage.ALERT_69,
				() => {
					downloadPersonalInfoButtonRef.current?.openModal();
				},
				NOOP
			);
			return Promise.resolve(false);
		}

		if (!hasFile) {
			showAlert(
				'파일을 저장하시겠습니까?',
				async () => {
					setIsLoading(true);
					await onSubmit?.((data) => {
						setIsLoading(false);

						if (!data?.isError) {
							downloadPersonalInfoButtonRef.current?.openModal();
						}
					}, false);
				},
				NOOP,
				{
					confirmText: 'yes',
					cancelText: 'no',
				}
			);
			return Promise.resolve(false);
		}

		return Promise.resolve(true);
	}, [hasErrorToDownload, hasFile, onSubmit]);

	const onSubmitPdf = useCallback(() => {
		setIsLoading(true);
		onSubmit?.(() => setIsLoading(false));
	}, [onSubmit]);

	const onPrint = useCallback(async () => {
		if (!pageRefs?.current) return;
		try {
			setIsLoading(true);
			await handlePrint({
				pageRefs,
				printIframeRef,
				printUrlRef,
				fileName,
			});
		} catch (error) {
			console.error('Error printing PDF:', error);
		} finally {
			setIsLoading(false);
		}
	}, [fileName]);

	useEffect(() => {
		if (containerRef && children) {
			containerRef.current?.addEventListener('scroll', handleScroll);
		}

		return () => {
			containerRef.current?.removeEventListener('scroll', handleScroll);
		};
	}, [containerRef.current, children]);

	useEffect(() => {
		return () => {
			// Clean up print resources when component unmounts
			if (printIframeRef.current) {
				document.body.removeChild(printIframeRef.current);
				printIframeRef.current = null;
			}
			if (printUrlRef.current) {
				URL.revokeObjectURL(printUrlRef.current);
				printUrlRef.current = null;
			}
		};
	}, []);

	return (
		<>
			<Loading isLoading={isLoading} className="h-full" />
			<div className="relative mt-5 mb-3">
				<div className="flex justify-center items-center gap-2">
					<Input
						value={page}
						inputClassName="px-1.5"
						containerClassName="!w-10 !h-10 flex-none"
						onChange={handlePageChange}
						maxLength={3}
						onPressEnter={onPressEnter}
						inputRef={inputRef}
					/>
					/ <span className="text-base font-normal text-font2">{count}</span>
				</div>

				<DownloadPersonalInfoButton
					ref={downloadPersonalInfoButtonRef}
					asChild
					onBeforeOpen={onBeforeDownloadOpen}
					onDownload={handleDownloadFile}
				>
					<DownloadButton onDownload={NOOP} />
				</DownloadPersonalInfoButton>

				<PrintButton onPrint={onPrint} className="right-20" />
			</div>
			<div className="overflow-auto h-[calc(100vh_-_188px)]" ref={containerRef}>
				{children}
			</div>

			<div className="flex justify-center items-center mt-5 pb-5">
				<Button type="primary" isMainBtn onClick={onSubmitPdf}>
					완료
				</Button>
			</div>
		</>
	);
};

export default memo(ContainerPdf);
